"use client";

import React, { useState } from "react";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import {
  TrashIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/20/solid";
import { SizeInput } from "./COBudgetItemsSection";

// ── SectionExpandPanel ────────────────────────────────────────────────────────33
function SectionExpandPanel({
  section,
  sectionIndex,
  isExpanded,
  isReadOnly,
  activeTab,
  onTabChange,
  changeItems,
  wasApproved,
  handleUpdateItem,
  handleRemoveItem,
  handleMoveItem,
  formatCurrency,
  isSFUnit,
  isLFUnit,
  isFromBudget,
  assemblyOptions,
  bidAreaOptions,
  referenceOptions,
  getLaborIdOptions,
  customStyles,
  sectionBreakdownTotals,
  sectionEstimatedCosts,
  formData,
  updateCustomPricingItem,
  removeCustomPricingItem,
  addCustomPricingItem,
  isTandMBased,
  tandmCostData,
  tandmNumber,
}) {
  if (!isExpanded || isReadOnly) return null;

  const sectionItems = changeItems.filter(
    (i) => i.referenceNumber === section.number,
  );
  const bd = sectionBreakdownTotals[section.number] || {
    materialItems: [],
    laborItems: [],
    materialTotal: 0,
    laborTotal: 0,
  };

  const renderFullRow = (item) => {
    const isEditable =
      (item.isFromDatabase || !item.sourceAssemblyId) && !wasApproved;
    const isCov =
      item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
    const coverage = parseFloat(item.coveragePerUnit) || 1;
    const pkgCost = isCov
      ? (item.unitCost || 0) * coverage
      : item.unitCost || 0;
    const globalIdx = changeItems.findIndex(
      (i) => i._localId === item._localId,
    );

    return (
      <tr key={item._localId} className="hover:bg-gray-600">
        <td className="px-2 py-2 text-gray-300 text-xs">
          {item.customerDescription || ""}
        </td>
        <td className="px-2 py-2">
          <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${item.category === "M" ? "bg-blue-500/20 text-blue-300" : "bg-purple-500/20 text-purple-300"}`}
          >
            {item.category === "M" ? "Mat" : "Lab"}
          </span>
        </td>
        <td className="px-2 py-2">
          {isEditable ? (
            <CreatableSelect
              options={assemblyOptions}
              value={
                assemblyOptions.find((o) => o.value === item.conditionName) ||
                (item.conditionName
                  ? { value: item.conditionName, label: item.conditionName }
                  : null)
              }
              onChange={(opt) =>
                handleUpdateItem(
                  item._localId,
                  "conditionName",
                  opt ? opt.value : "",
                )
              }
              onCreateOption={(v) =>
                handleUpdateItem(item._localId, "conditionName", v)
              }
              placeholder="Assembly..."
              isClearable
              formatCreateLabel={(v) => `Create: "${v}"`}
              className="min-w-40"
              styles={customStyles}
              menuPortalTarget={document.body}
            />
          ) : (
            <span className="text-xs text-gray-300">
              {item.conditionName || "—"}
            </span>
          )}
        </td>
        <td className="px-2 py-2">
          {isEditable ? (
            <CreatableSelect
              options={bidAreaOptions}
              value={
                bidAreaOptions.find((o) => o.value === item.bidArea) ||
                (item.bidArea
                  ? { value: item.bidArea, label: item.bidArea }
                  : null)
              }
              onChange={(opt) =>
                handleUpdateItem(item._localId, "bidArea", opt ? opt.value : "")
              }
              onCreateOption={(v) =>
                handleUpdateItem(item._localId, "bidArea", v)
              }
              className="min-w-36"
              placeholder="Bid area..."
              isClearable
              formatCreateLabel={(v) => `Create: "${v}"`}
              styles={customStyles}
              menuPortalTarget={document.body}
            />
          ) : (
            <span className="text-xs text-gray-300">{item.bidArea || "—"}</span>
          )}
        </td>
        <td className="px-2 py-2">
          {isEditable && isCov ? (
            <SizeInput
              value={item.size || ""}
              onCommit={(v) => handleUpdateItem(item._localId, "size", v)}
              placeholder={isSFUnit(item.uom) ? "4'x8'" : "10'"}
            />
          ) : isCov ? (
            <span className="text-xs text-gray-300 font-mono">
              {item.size || "-"}
            </span>
          ) : (
            <span className="text-xs text-gray-500">-</span>
          )}
        </td>
        <td className="px-2 py-2 text-right">
          <div className="flex flex-col items-end">
            <span
              className={`text-xs ${item.hasPriceChange ? "text-purple-400" : "text-gray-300"}`}
            >
              {formatCurrency(item.unitCost)}
            </span>
            {item.hasPriceChange && item.originalPrice !== item.unitCost && (
              <span className="text-[10px] text-gray-500 line-through">
                {formatCurrency(item.originalPrice)}
              </span>
            )}
          </div>
        </td>
        <td className="px-2 py-2 text-right text-xs text-gray-300">
          {item.uom}
        </td>
        <td className="px-2 py-2 text-right">
          <span className="text-xs font-medium text-orange-300">
            {formatCurrency(pkgCost)}
          </span>
        </td>
        <td className="px-2 py-2">
          <div className="flex flex-col gap-0.5">
            <input
              type="text"
              inputMode="decimal"
              value={
                item._rawDeltaInput !== undefined
                  ? item._rawDeltaInput
                  : item.deltaQty || 0
              }
              onChange={(e) =>
                handleUpdateItem(item._localId, "deltaQty", e.target.value)
              }
              onBlur={() => {
                if (
                  item._rawDeltaInput !== undefined ||
                  item.deltaQty === "" ||
                  item.deltaQty === "-"
                )
                  handleUpdateItem(item._localId, "deltaQty", "0");
              }}
              disabled={wasApproved}
              className={`w-20 px-2 py-1 text-right rounded border text-xs font-medium ${(item.deltaQty || 0) < 0 ? "text-red-400" : "text-gray-200"} ${wasApproved ? "bg-gray-700 opacity-60" : "bg-gray-600"} border-gray-500`}
            />
            {isFromBudget(item) && item.budgetedQty > 0 && (
              <span className="text-[9px] text-gray-500 text-center">{`min: -${item.budgetedQty}`}</span>
            )}
          </div>
        </td>
        <td className="px-2 py-2 text-center">
          <span className="text-xs text-gray-300">
            {isCov ? "PC" : item.uom || "EA"}
          </span>
        </td>
        <td className="px-2 py-2 text-right">
          <span
            className={`text-xs font-medium ${item.amount < 0 ? "text-red-400" : "text-gray-200"}`}
          >
            {formatCurrency(item.amount)}
          </span>
        </td>
        <td className="px-2 py-2">
          {item.conditionName ? (
            isFromBudget(item) ? (
              <div className="text-xs text-gray-400 bg-gray-800 rounded px-2 py-1">
                {item.laborId || "—"}
              </div>
            ) : (
              <Select
                options={getLaborIdOptions(item.conditionName)}
                value={
                  getLaborIdOptions(item.conditionName).find(
                    (o) => o.value === item.laborId,
                  ) || null
                }
                onChange={(opt) =>
                  handleUpdateItem(item._localId, "laborId", opt?.value ?? null)
                }
                placeholder="Select"
                isClearable
                styles={customStyles}
                menuPortalTarget={document.body}
                className="w-24"
              />
            )
          ) : (
            <span className="text-[10px] text-gray-500">—</span>
          )}
        </td>
        <td className="px-2 py-2 text-center">
          <button
            type="button"
            onClick={() =>
              handleUpdateItem(
                item._localId,
                "includeInBreakdown",
                !item.includeInBreakdown,
              )
            }
            disabled={!item.referenceNumber}
            className={`relative w-9 h-5 rounded-full transition-colors ${!item.referenceNumber ? "bg-gray-600 cursor-not-allowed opacity-50" : item.includeInBreakdown ? "bg-green-500" : "bg-gray-500"}`}
          >
            <span
              className={`absolute w-3.5 h-3.5 bg-white rounded-full top-[3px] transition-transform ${item.includeInBreakdown ? "translate-x-[3px]" : "translate-x-[-14px]"}`}
            />
          </button>
        </td>
        <td className="px-2 py-2 text-center">
          <div className="flex flex-col items-center gap-0.5">
            <button
              type="button"
              onClick={() => handleMoveItem(item._localId, "up")}
              disabled={globalIdx === 0}
              className="text-gray-400 hover:text-orange-400 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronUpIcon className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => handleMoveItem(item._localId, "down")}
              disabled={globalIdx === changeItems.length - 1}
              className="text-gray-400 hover:text-orange-400 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronDownIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </td>
        {!wasApproved && (
          <td className="px-2 py-2">
            <button
              type="button"
              onClick={() => handleRemoveItem(item._localId)}
              className="text-red-400 hover:text-red-500"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </td>
        )}
      </tr>
    );
  };

  const renderBreakdownTable = (items, category) => {
    const isM = category === "M";
    const color = isM ? "blue" : "purple";
    const total = isM ? bd.materialTotal : bd.laborTotal;
    return (
      <div className={isM ? "mb-6" : ""}>
        <div className="flex items-center justify-between mb-2">
          <div className={`text-xs text-${color}-400 font-medium`}>
            {isM ? "Material" : "Labor"} Items:
          </div>
          <button
            type="button"
            onClick={() => addCustomPricingItem(sectionIndex, category)}
            className={`px-2 py-1 text-xs text-${color}-400 bg-${color}-500/10 border border-${color}-500/30 rounded hover:bg-${color}-500/20 flex items-center`}
          >
            <PlusIcon className="h-3 w-3 mr-1" />
            Add {isM ? "Material" : "Labor"} Item
          </button>
        </div>
        {items?.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="text-left py-1 w-8"></th>
                <th className="text-left py-1">Description</th>
                <th className="text-right py-1 w-20">Qty</th>
                <th className="text-center py-1 w-20">Unit</th>
                <th className="text-right py-1 w-24">Unit Price</th>
                <th className="text-right py-1 w-24">Total</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item._localId || item._id}
                  className="border-t border-gray-700"
                >
                  <td className="py-2">
                    <span
                      className={`w-5 h-5 ${item.isCustom ? "bg-yellow-500/20" : isM ? "bg-blue-500/20" : "bg-purple-500/20"} rounded flex items-center justify-center`}
                    >
                      <span
                        className={`text-${item.isCustom ? "yellow" : isM ? "blue" : "purple"}-400 text-[10px] font-bold`}
                      >
                        {item.isCustom ? "C" : "B"}
                      </span>
                    </span>
                  </td>
                  <td className="py-2">
                    <input
                      type="text"
                      value={item.breakdownDescription}
                      onChange={(e) =>
                        item.isCustom
                          ? updateCustomPricingItem(
                              sectionIndex,
                              item._id,
                              "description",
                              e.target.value,
                            )
                          : handleUpdateItem(
                              item._localId,
                              "breakdownDescription",
                              e.target.value,
                            )
                      }
                      placeholder="Enter description..."
                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm"
                    />
                  </td>
                  <td className="py-2">
                    <input
                      type="number"
                      step="0.01"
                      value={item.breakdownQty}
                      onChange={(e) =>
                        item.isCustom
                          ? updateCustomPricingItem(
                              sectionIndex,
                              item._id,
                              "qty",
                              parseFloat(e.target.value) || 0,
                            )
                          : handleUpdateItem(
                              item._localId,
                              "breakdownQty",
                              e.target.value,
                            )
                      }
                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm text-right"
                    />
                  </td>
                  <td className="py-2">
                    <input
                      type="text"
                      value={item.breakdownUnit}
                      onChange={(e) =>
                        item.isCustom
                          ? updateCustomPricingItem(
                              sectionIndex,
                              item._id,
                              "unit",
                              e.target.value,
                            )
                          : handleUpdateItem(
                              item._localId,
                              "breakdownUnit",
                              e.target.value,
                            )
                      }
                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm text-center"
                    />
                  </td>
                  <td className="py-2">
                    <input
                      type="number"
                      step="0.01"
                      value={item.breakdownUnitPrice}
                      onChange={(e) =>
                        item.isCustom
                          ? updateCustomPricingItem(
                              sectionIndex,
                              item._id,
                              "unitPrice",
                              parseFloat(e.target.value) || 0,
                            )
                          : handleUpdateItem(
                              item._localId,
                              "breakdownUnitPrice",
                              e.target.value,
                            )
                      }
                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm text-right"
                    />
                  </td>
                  <td className="py-2 text-right text-gray-300 font-medium">
                    {formatCurrency(item.breakdownTotal)}
                  </td>
                  <td className="py-2 text-center">
                    {item.isCustom && (
                      <button
                        type="button"
                        onClick={() =>
                          removeCustomPricingItem(sectionIndex, item._id)
                        }
                        className="text-red-400 hover:text-red-300 p-1"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-600">
                <td
                  colSpan={5}
                  className="py-2 text-right text-gray-400 font-medium"
                >
                  {isM ? "Material" : "Labor"} Total:
                </td>
                <td className={`py-2 text-right text-${color}-400 font-bold`}>
                  {formatCurrency(total)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div className="text-center text-gray-500 py-4 border border-dashed border-gray-600 rounded">
            No {isM ? "material" : "labor"} items. Click "Add{" "}
            {isM ? "Material" : "Labor"} Item" to add one.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border-t border-gray-700 bg-gray-900/50">
      <div className="flex border-b border-gray-700 px-4">
        {["items", "breakdown"].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => onTabChange(tab)}
            className={`px-4 py-2 text-xs font-medium transition-colors ${activeTab === tab ? "text-orange-400 border-b-2 border-orange-400" : "text-gray-400 hover:text-gray-200"}`}
          >
            {tab === "items" ? "Budget Items" : "Breakdown"}
            <span className="ml-1.5 px-1.5 py-0.5 text-[9px] rounded-full bg-gray-700 text-gray-300">
              {tab === "items"
                ? sectionItems.length
                : (bd.materialItems?.length || 0) +
                  (bd.laborItems?.length || 0)}
            </span>
          </button>
        ))}
      </div>

      {/* ── Budget Items tab ── */}
      {activeTab === "items" && (
        <div className="overflow-x-auto">
          {/* T&M cost breakdown — internal cost reference for PM, not customer-facing */}
          {isTandMBased && tandmCostData?.groups?.length > 0 && (
            <div className="px-4 py-3 border-b border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <svg
                    className="h-3.5 w-3.5 text-teal-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 01-2.828-2.828l3-3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-xs font-semibold text-teal-300 uppercase tracking-wide">
                    T&M #{tandmNumber} — Cost Base
                  </span>
                </div>
                <span className="text-xs font-bold text-teal-400 tabular-nums">
                  {formatCurrency(tandmCostData.totals?.totalCost || 0)}
                </span>
              </div>

              <div className="rounded-lg border border-teal-700/40 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-teal-900/30 border-b border-teal-700/40">
                      <th className="text-left px-3 py-2 text-teal-400 font-medium">
                        Assembly / Description
                      </th>
                      <th className="text-right px-3 py-2 text-teal-400 font-medium w-24">
                        Qty
                      </th>
                      <th className="text-right px-3 py-2 text-teal-400 font-medium w-28">
                        Unit Cost
                      </th>
                      <th className="text-right px-3 py-2 text-teal-400 font-medium w-28">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tandmCostData.groups.map((group) => (
                      <React.Fragment key={group.conditionName}>
                        {/* Assembly group header */}
                        <tr className="bg-gray-800/50 border-t border-teal-700/20">
                          <td colSpan={4} className="px-3 py-1.5">
                            <span className="font-semibold text-teal-300/80 text-[11px]">
                              {group.conditionName}
                            </span>
                            <span className="ml-2 text-gray-500 text-[10px]">
                              {group.items.length} item
                              {group.items.length !== 1 ? "s" : ""}
                            </span>
                          </td>
                        </tr>
                        {/* Line items */}
                        {group.items.map((li) => (
                          <tr
                            key={li._id}
                            className="border-t border-gray-700/40 hover:bg-gray-800/30"
                          >
                            <td className="px-3 py-1.5 text-gray-300">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className={`inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-bold shrink-0 ${li.category === "M" ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"}`}
                                >
                                  {li.category === "M" ? "M" : "L"}
                                </span>
                                <span>{li.itemDescription || "—"}</span>
                                {li.size && (
                                  <span className="text-gray-500 font-mono text-[10px]">
                                    {li.size}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-1.5 text-right text-gray-400 tabular-nums">
                              {li.quantity != null
                                ? `${li.quantity}${li.uom ? ` ${li.uom}` : ""}`
                                : "—"}
                            </td>
                            <td className="px-3 py-1.5 text-right text-gray-400 tabular-nums">
                              {formatCurrency(li.unitCost || 0)}
                            </td>
                            <td className="px-3 py-1.5 text-right text-gray-200 tabular-nums font-medium">
                              {formatCurrency(li.amount || 0)}
                            </td>
                          </tr>
                        ))}
                        {/* Group subtotal */}
                        <tr className="border-t border-teal-700/20 bg-teal-900/10">
                          <td
                            colSpan={3}
                            className="px-3 py-1 text-right text-teal-400/60 text-[10px] uppercase tracking-wide"
                          >
                            {group.conditionName} subtotal
                          </td>
                          <td className="px-3 py-1 text-right text-teal-400 font-semibold tabular-nums">
                            {formatCurrency(
                              (group.materialTotal || 0) +
                                (group.laborTotal || 0),
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-teal-600/50 bg-teal-900/20">
                      <td
                        colSpan={3}
                        className="px-3 py-2 text-right text-teal-300 font-semibold text-xs"
                      >
                        T&M Total Cost
                      </td>
                      <td className="px-3 py-2 text-right text-teal-300 font-bold tabular-nums">
                        {formatCurrency(tandmCostData.totals?.totalCost || 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {sectionItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No budget items assigned to this section.
              {!wasApproved && (
                <p className="text-xs mt-1 text-gray-600">
                  Use the "Add items to this section" buttons above, or set the
                  Ref # on any item in the main table.
                </p>
              )}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-600">
              <thead className="bg-gray-800">
                <tr className="border-b border-gray-700">
                  {[
                    "Description",
                    "Cat",
                    "Assembly",
                    "Bid Area",
                    "Size",
                    "Unit Cost",
                    "Unit",
                    "Pkg Cost",
                    "Qty Δ",
                    "Pkg Unit",
                    "Total Cost",
                    "Labor ID",
                    "Breakdown",
                    "Order",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-2 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                  {!wasApproved && <th className="px-2 py-2 w-10" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {sectionItems.map(renderFullRow)}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-600 bg-gray-800/60">
                  <td
                    colSpan={10}
                    className="px-2 py-2 text-right text-xs text-gray-400 font-medium"
                  >
                    Section cost total:
                  </td>
                  <td className="px-2 py-2 text-right text-sm font-bold text-green-400 tabular-nums">
                    {formatCurrency(
                      sectionItems.reduce((s, i) => s + (i.amount || 0), 0),
                    )}
                  </td>
                  <td colSpan={wasApproved ? 2 : 3} />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* ── Breakdown tab — customer-facing pricing only ── */}
      {activeTab === "breakdown" && (
        <div className="px-4 py-3">
          {renderBreakdownTable(bd.materialItems, "M")}
          {renderBreakdownTable(bd.laborItems, "L")}
        </div>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function COPricingSections({
  pricingSections,
  changeItems,
  wasApproved,
  isReadOnly,
  viewingRevision,
  displaySections,
  formData,
  expandedSections,
  toggleSectionExpand,
  addPricingSection,
  removePricingSection,
  updatePricingSection,
  sectionBreakdownTotals,
  sectionEstimatedCosts,
  formatCurrency,
  hasBreakdownItems,
  getSectionTotal,
  handleSectionAdd,
  sectionActiveTab,
  setSectionActiveTab,
  addCustomPricingItem,
  updateCustomPricingItem,
  removeCustomPricingItem,
  isTandMBased,
  tandmCostData,
  tandmNumber,
  // Passed through to SectionExpandPanel
  handleUpdateItem,
  handleRemoveItem,
  handleMoveItem,
  isSFUnit,
  isLFUnit,
  isFromBudget,
  assemblyOptions,
  bidAreaOptions,
  referenceOptions,
  getLaborIdOptions,
  customStyles,
}) {
  return (
    <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-white">
          Customer Pricing Sections
          {wasApproved && (
            <span className="ml-2 text-sm font-normal text-green-400">
              — Editable
            </span>
          )}
        </h2>
        {!viewingRevision && (
          <button
            type="button"
            onClick={addPricingSection}
            className="px-4 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700"
          >
            <PlusIcon className="h-4 w-4 inline mr-1" />
            Add Section
          </button>
        )}
      </div>

      {displaySections.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No pricing sections yet.
        </div>
      ) : (
        <div className="space-y-4">
          {displaySections.map((section, index) => {
            const isExpanded = expandedSections.has(section.number);
            const estCosts = sectionEstimatedCosts[section.number] || {
              material: 0,
              labor: 0,
            };
            const markup = formData.defaultMarkupPercentage || 0;

            const tandmBasePerSection =
              isTandMBased && tandmCostData?.totals?.totalCost
                ? tandmCostData.totals.totalCost /
                  Math.max(displaySections.length, 1)
                : 0;
            const effectiveEstCostMaterial = estCosts.material;
            const effectiveEstCostLabor = estCosts.labor;
            const effectiveEstCostTotal =
              effectiveEstCostMaterial +
              effectiveEstCostLabor +
              (isTandMBased ? tandmBasePerSection : 0);

            return (
              <div
                key={section.number}
                className="bg-gray-800 rounded-lg border border-gray-600 overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-lg">
                        {section.number}
                      </span>
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) =>
                          updatePricingSection(index, "title", e.target.value)
                        }
                        placeholder="Section title..."
                        className="w-full rounded px-3 py-2 text-gray-200 text-sm bg-gray-700 border border-gray-600"
                      />
                    </div>
                    <div className="text-right w-32">
                      <div className="text-xs text-gray-500">Total Price</div>
                      <div
                        className={`text-2xl font-bold ${getSectionTotal(section.number) < 0 ? "text-red-400" : "text-green-400"}`}
                      >
                        {formatCurrency(getSectionTotal(section.number))}
                        {getSectionTotal(section.number) < 0 && (
                          <span className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 align-middle">
                            Credit
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Suggested
                      </div>
                      <div className="text-sm text-orange-300">
                        {formatCurrency(
                          effectiveEstCostTotal * (1 + markup / 100),
                        )}
                      </div>
                    </div>
                    <div className="text-right w-24">
                      <div className="text-xs text-gray-500">Margin</div>
                      {(() => {
                        const tp =
                          (sectionBreakdownTotals[section.number]
                            ?.materialTotal || 0) +
                          (sectionBreakdownTotals[section.number]?.laborTotal ||
                            0);
                        const ec = effectiveEstCostTotal;
                        const mp = tp > 0 ? ((tp - ec) / tp) * 100 : 0;
                        return (
                          <div
                            className={`text-sm font-semibold ${mp >= 0 ? "text-green-400" : "text-red-400"}`}
                          >
                            {mp.toFixed(1)}%
                          </div>
                        );
                      })()}
                    </div>
                    {!viewingRevision && (
                      <>
                        <button
                          type="button"
                          onClick={() => toggleSectionExpand(index)}
                          className="p-2 text-gray-400 hover:text-orange-400 hover:bg-gray-700 rounded-lg"
                        >
                          {isExpanded ? (
                            <ChevronUpIcon className="h-5 w-5" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5" />
                          )}
                        </button>
                        {!wasApproved && (
                          <button
                            type="button"
                            onClick={() => removePricingSection(index)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Section-scoped add buttons */}
                  {!wasApproved && !viewingRevision && (
                    <div className="mt-3 pl-14 flex items-center gap-2">
                      <span className="text-[11px] text-gray-500 font-medium uppercase tracking-wide mr-1">
                        Add items to this section:
                      </span>
                      {[
                        [
                          "budget",
                          "From Budget",
                          "bg-orange-500 hover:bg-orange-600",
                        ],
                        [
                          "database",
                          "From Database",
                          "bg-blue-600 hover:bg-blue-700",
                        ],
                        [
                          "excel",
                          "From Excel",
                          "bg-purple-600 hover:bg-purple-700",
                        ],
                      ].map(([type, label, cls]) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => handleSectionAdd(section.number, type)}
                          className={`px-3 py-1.5 text-sm font-medium text-white rounded-md flex items-center gap-1.5 shadow-sm ${cls}`}
                        >
                          {type === "excel" ? (
                            <ArrowUpTrayIcon className="h-4 w-4" />
                          ) : (
                            <PlusIcon className="h-4 w-4" />
                          )}
                          {label}
                        </button>
                      ))}
                      {changeItems.filter(
                        (i) => i.referenceNumber === section.number,
                      ).length > 0 && (
                        <span className="ml-2 text-xs text-gray-400">
                          {
                            changeItems.filter(
                              (i) => i.referenceNumber === section.number,
                            ).length
                          }{" "}
                          item
                          {changeItems.filter(
                            (i) => i.referenceNumber === section.number,
                          ).length !== 1
                            ? "s"
                            : ""}{" "}
                          assigned
                        </span>
                      )}
                    </div>
                  )}

                  {/* Material & Labor price inputs */}
                  <div className="mt-4 grid grid-cols-2 gap-4 pl-14">
                    {[
                      ["M", "Material", "blue"],
                      ["L", "Labor", "purple"],
                    ].map(([cat, label, color]) => {
                      const isMatType = cat === "M";
                      const priceField = isMatType
                        ? "materialPrice"
                        : "laborPrice";
                      const estCost = isMatType
                        ? effectiveEstCostMaterial
                        : effectiveEstCostLabor;
                      const bdTotal = isMatType
                        ? sectionBreakdownTotals[section.number]?.materialTotal
                        : sectionBreakdownTotals[section.number]?.laborTotal;
                      const bdItems = isMatType
                        ? sectionBreakdownTotals[section.number]?.materialItems
                        : sectionBreakdownTotals[section.number]?.laborItems;
                      const hasBreakdown = hasBreakdownItems(
                        section.number,
                        cat,
                      );

                      return (
                        <div
                          key={cat}
                          className="bg-gray-700/50 rounded-lg p-3 border border-gray-600"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className={`w-6 h-6 bg-${color}-500/20 rounded flex items-center justify-center`}
                            >
                              <span
                                className={`text-${color}-400 text-xs font-bold`}
                              >
                                {cat}
                              </span>
                            </div>
                            <span className="text-sm text-gray-400">
                              {section.number}.{isMatType ? "1" : "2"} {label}{" "}
                              Price
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            {hasBreakdown ? (
                              <div className="flex-1">
                                <div className="flex items-baseline gap-3">
                                  <span
                                    className={`text-2xl font-bold text-${color}-400`}
                                  >
                                    {formatCurrency(bdTotal || 0)}
                                  </span>
                                  <span className="text-xs text-gray-500 italic">
                                    (from breakdown)
                                  </span>
                                </div>
                                <div className="mt-1 text-xs text-gray-400">
                                  {bdItems?.length || 0} {label.toLowerCase()}{" "}
                                  item{(bdItems?.length || 0) !== 1 ? "s" : ""}
                                </div>
                              </div>
                            ) : viewingRevision ? (
                              <span
                                className={`text-2xl font-bold text-${color}-400`}
                              >
                                {formatCurrency(section[priceField] || 0)}
                              </span>
                            ) : (
                              <input
                                type="number"
                                step="0.01"
                                value={section[priceField] ?? 0}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "" || val === "-") return;
                                  const parsed = parseFloat(val);
                                  updatePricingSection(
                                    index,
                                    priceField,
                                    isNaN(parsed) ? 0 : parsed,
                                  );
                                }}
                                onBlur={(e) => {
                                  const parsed = parseFloat(e.target.value);
                                  updatePricingSection(
                                    index,
                                    priceField,
                                    isNaN(parsed) ? 0 : parsed,
                                  );
                                }}
                                className={`w-40 text-right text-2xl font-bold bg-gray-800 border rounded px-3 py-2 focus:outline-none focus:ring-2
    ${
      (section[priceField] ?? 0) < 0
        ? "text-red-400 border-red-500/50 focus:ring-red-500"
        : `text-${color}-300 border-gray-600 focus:ring-${color}-500`
    }`}
                                placeholder="0.00"
                              />
                            )}
                            <div className="text-right ml-auto">
                              <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                                Est. Cost
                              </div>
                              <div className="text-sm text-gray-300">
                                {formatCurrency(estCost)}
                              </div>
                              <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-1">
                                Suggested ({markup}% markup)
                              </div>
                              <div className="text-sm text-orange-300">
                                {formatCurrency(estCost * (1 + markup / 100))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* T&M cost base note on the section card */}
                  {isTandMBased && tandmCostData?.totals?.totalCost > 0 && (
                    <div className="mt-3 pl-14">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal-900/20 border border-teal-700/30 text-xs text-teal-400">
                        <svg
                          className="h-3 w-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 01-2.828-2.828l3-3z"
                            clipRule="evenodd"
                          />
                        </svg>
                        T&M cost base:{" "}
                        {formatCurrency(tandmCostData.totals.totalCost)} — see
                        Budget Items tab for line-item detail
                      </div>
                    </div>
                  )}
                </div>

                <SectionExpandPanel
                  section={section}
                  sectionIndex={index}
                  isExpanded={isExpanded}
                  isReadOnly={isReadOnly}
                  activeTab={sectionActiveTab[section.number] || "items"}
                  onTabChange={(tab) =>
                    setSectionActiveTab((p) => ({
                      ...p,
                      [section.number]: tab,
                    }))
                  }
                  changeItems={changeItems}
                  wasApproved={wasApproved}
                  handleUpdateItem={handleUpdateItem}
                  handleRemoveItem={handleRemoveItem}
                  handleMoveItem={handleMoveItem}
                  formatCurrency={formatCurrency}
                  isSFUnit={isSFUnit}
                  isLFUnit={isLFUnit}
                  isFromBudget={isFromBudget}
                  assemblyOptions={assemblyOptions}
                  bidAreaOptions={bidAreaOptions}
                  referenceOptions={referenceOptions}
                  getLaborIdOptions={getLaborIdOptions}
                  customStyles={customStyles}
                  sectionBreakdownTotals={sectionBreakdownTotals}
                  sectionEstimatedCosts={sectionEstimatedCosts}
                  formData={formData}
                  updateCustomPricingItem={updateCustomPricingItem}
                  removeCustomPricingItem={removeCustomPricingItem}
                  addCustomPricingItem={addCustomPricingItem}
                  isTandMBased={isTandMBased}
                  tandmCostData={tandmCostData}
                  tandmNumber={tandmNumber}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
